(function () {
  const CM_PER_INCH = 2.54;

  const UK_CUP_ORDER = ['A', 'B', 'C', 'D', 'DD', 'E', 'F', 'FF', 'G', 'GG', 'H', 'HH'];

  const UK_CUP_BY_DIFFERENCE = {
    1: 'A',
    2: 'B',
    3: 'C',
    4: 'D',
    5: 'DD',
    6: 'E',
    7: 'F',
    8: 'FF',
    9: 'G',
    10: 'GG',
    11: 'H',
    12: 'HH',
  };

  function splidePaginationPlaceholderExtension(Splide) {
    function mount() {
      Splide.root.querySelectorAll('[data-splide-pagination-placeholder]').forEach((element) => {
        element.remove();
      });
    }

    return { mount };
  }

  function parseJsonScript(root, selector) {
    const script = root.querySelector(selector);
    if (!script) return null;

    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      console.error(`Stana size calculator: invalid JSON in ${selector}`, error);
      return null;
    }
  }

  function parseVariants(root) {
    const parsed = parseJsonScript(root, '[data-stana-size-calculator-data]');
    return Array.isArray(parsed) ? parsed : [];
  }

  function parseLookup(root) {
    return parseJsonScript(root, '[data-stana-sizing-chart-lookup]') || {};
  }

  function toInches(value, unit) {
    const number = parseFloat(value);
    if (Number.isNaN(number)) return null;
    return unit === 'cm' ? number / CM_PER_INCH : number;
  }

  function bandFromUnderband(underbandInches) {
    const rounded = Math.round(underbandInches);
    if (rounded <= 0) return null;
    return rounded % 2 === 0 ? rounded : rounded + 1;
  }

  function cupFromDifference(differenceInches) {
    const rounded = Math.round(differenceInches);
    return UK_CUP_BY_DIFFERENCE[rounded] || null;
  }

  function normalizeBraSize(value) {
    if (!value) return '';
    return String(value).toUpperCase().replace(/\s+/g, '');
  }

  function formatBraSize(band, cup) {
    if (!band || !cup) return '';
    return `${band}${cup}`;
  }

  function parseVariantTitle(title) {
    if (!title) return null;

    const match = title.match(/^(\d+)\s*\(([^)]+)\)/);
    if (!match) return null;

    const stanaSize = parseInt(match[1], 10);
    const braSizes = match[2]
      .split('/')
      .map((part) => normalizeBraSize(part.trim()))
      .filter(Boolean);

    if (!stanaSize || braSizes.length === 0) return null;

    return { stanaSize, braSizes };
  }

  function enrichVariants(variants) {
    return variants
      .map((variant) => {
        const parsed = parseVariantTitle(variant.variantTitle);
        if (!parsed) return null;

        return {
          ...variant,
          stanaSize: parsed.stanaSize,
          braSizes: parsed.braSizes,
        };
      })
      .filter(Boolean);
  }

  function lookupStanaSize(lookup, band, cup) {
    const bandKey = String(band);
    const cupKey = cup.toUpperCase();
    const row = lookup[bandKey];
    if (!row) return null;
    const size = row[cupKey];
    return size == null ? null : Number(size);
  }

  function getSupportedRange(lookup) {
    const bands = Object.keys(lookup)
      .map((band) => parseInt(band, 10))
      .filter((band) => !Number.isNaN(band))
      .sort((a, b) => a - b);

    const cups = new Set();
    const stanaSizes = new Set();

    bands.forEach((band) => {
      Object.entries(lookup[String(band)] || {}).forEach(([cup, stanaSize]) => {
        cups.add(cup.toUpperCase());
        stanaSizes.add(Number(stanaSize));
      });
    });

    const sortedCups = [...cups].sort(
      (a, b) => UK_CUP_ORDER.indexOf(a) - UK_CUP_ORDER.indexOf(b)
    );

    return {
      minBand: bands[0] ?? '',
      maxBand: bands[bands.length - 1] ?? '',
      minCup: sortedCups[0] ?? '',
      maxCup: sortedCups[sortedCups.length - 1] ?? '',
      minStana: stanaSizes.size ? Math.min(...stanaSizes) : '',
      maxStana: stanaSizes.size ? Math.max(...stanaSizes) : '',
    };
  }

  function readTemplate(root, selector, fallback = '') {
    return root.querySelector(selector)?.textContent.trim() || fallback;
  }

  const DEFAULT_UNSUPPORTED_TEMPLATE =
    '<p>We couldn\'t find a match for {bra_size}. Stana currently supports UK bra sizes from {min_band}{min_cup} to {max_band}{max_cup}. If you think your size should be supported, find our sizes on <a href="{sizing_chart_url}">our size chart</a>.</p>';

  const DEFAULT_NO_RESULTS_TEMPLATE =
    '<p>We couldn\'t find a match in our current collection. Stana currently supports UK bra sizes from {min_band}{min_cup} to {max_band}{max_cup}. Find our sizes on <a href="{sizing_chart_url}">our size chart</a>.</p>';

  function formatMessage(template, context) {
    const sizingChartUrl = `#${context.sizingChartHash || 'sizing'}`;

    return template
      .replace(/\{bra_size\}/g, context.braSize || '')
      .replace(/\{min_stana\}/g, String(context.minStana))
      .replace(/\{max_stana\}/g, String(context.maxStana))
      .replace(/\{min_band\}/g, String(context.minBand))
      .replace(/\{max_band\}/g, String(context.maxBand))
      .replace(/\{min_cup\}/g, String(context.minCup))
      .replace(/\{max_cup\}/g, String(context.maxCup))
      .replace(/\{sizing_chart_url\}/g, sizingChartUrl);
  }

  function buildMessageContext(range, sizingChartHash, braSize) {
    return {
      ...range,
      braSize: braSize || '',
      sizingChartHash,
    };
  }

  function resolveSizeFromMeasurements(underbandInches, bustInches, lookup) {
    const band = bandFromUnderband(underbandInches);
    const cup = cupFromDifference(bustInches - underbandInches);

    if (!band || !cup) {
      return { status: 'invalid' };
    }

    const braSize = formatBraSize(band, cup);
    const stanaSize = lookupStanaSize(lookup, band, cup);

    if (stanaSize == null) {
      return { status: 'unsupported', braSize, band, cup };
    }

    return {
      status: 'ok',
      sizeResult: { band, cup, braSize, stanaSize },
    };
  }

  function parseBraSizeInput(value) {
    const normalized = normalizeBraSize(value);
    const match = normalized.match(/^(\d{2,3})([A-Z]+)$/);
    if (!match) return null;

    return {
      band: parseInt(match[1], 10),
      cup: match[2],
      braSize: normalized,
    };
  }

  function resolveSizeFromBraInput(value, lookup) {
    const parsed = parseBraSizeInput(value);
    if (!parsed) {
      return { status: 'invalid' };
    }

    const stanaSize = lookupStanaSize(lookup, parsed.band, parsed.cup);
    if (stanaSize == null) {
      return {
        status: 'unsupported',
        braSize: parsed.braSize,
        band: parsed.band,
        cup: parsed.cup,
      };
    }

    return {
      status: 'ok',
      sizeResult: {
        band: parsed.band,
        cup: parsed.cup,
        braSize: parsed.braSize,
        stanaSize,
      },
    };
  }

  function matchVariants(variants, sizeResult) {
    const targetBra = normalizeBraSize(sizeResult.braSize);
    const targetStana = Number(sizeResult.stanaSize);

    return variants.filter((variant) => {
      if (variant.stanaSize === targetStana) return true;
      return variant.braSizes.some((braSize) => normalizeBraSize(braSize) === targetBra);
    });
  }

  function displayTitle(item) {
    if (item.variantTitle && item.variantTitle !== 'Default Title') {
      return `${item.productTitle} — ${item.variantTitle}`;
    }

    return item.productTitle;
  }

  function buildSlide(item) {
    const slide = document.createElement('li');
    slide.className = 'splide__slide stana-size-calculator__result-slide';

    const link = document.createElement('a');
    link.className = 'stana-size-calculator__result-tile';
    link.href = item.url;

    const media = document.createElement('div');
    media.className = 'stana-size-calculator__result-media';

    if (item.image) {
      const image = document.createElement('img');
      image.src = item.image;
      image.alt = displayTitle(item);
      image.loading = 'lazy';
      media.appendChild(image);
    }

    const title = document.createElement('p');
    title.className = 'stana-size-calculator__result-title';
    title.textContent = displayTitle(item);

    link.appendChild(media);
    link.appendChild(title);
    slide.appendChild(link);

    return slide;
  }

  function resetPagination(pagination) {
    if (!pagination) return;
    pagination.innerHTML = '';
  }

  function destroyCarousel(root) {
    const existing = root.__stanaCalculatorSplide;
    if (existing) {
      existing.destroy(true);
      root.__stanaCalculatorSplide = null;
    }

    const controls = root.querySelector('[data-stana-calculator-controls]');
    if (controls) controls.hidden = true;

    resetPagination(root.querySelector('[data-stana-calculator-pagination]'));
  }

  function updateControlsVisibility(splide, controls, matchCount) {
    if (!controls) return;

    if (matchCount <= 1 || !splide) {
      controls.hidden = true;
      return;
    }

    controls.hidden = !splide.root.classList.contains('is-overflow');
  }

  function initCarousel(root, matchCount) {
    const carousel = root.querySelector('[data-stana-calculator-carousel]');
    const controls = root.querySelector('[data-stana-calculator-controls]');
    if (!carousel || matchCount === 0 || typeof window.Splide === 'undefined') return;

    destroyCarousel(root);

    const showControls = matchCount > 1;

    root.__stanaCalculatorSplide = new window.Splide(carousel, {
      autoWidth: true,
      gap: '1rem',
      arrows: showControls,
      pagination: showControls,
      drag: true,
      rewind: false,
    });

    const syncControls = () =>
      updateControlsVisibility(root.__stanaCalculatorSplide, controls, matchCount);

    root.__stanaCalculatorSplide.on('mounted resize overflow updated', syncControls);

    root.__stanaCalculatorSplide.mount({
      splidePaginationPlaceholderExtension: splidePaginationPlaceholderExtension,
    });

    syncControls();
  }

  function setResultsSizePill(root, stanaSize) {
    const resultsSizePill = root.querySelector('[data-stana-calculator-results-size]');
    if (!resultsSizePill) return;

    if (stanaSize == null) {
      resultsSizePill.hidden = true;
      resultsSizePill.textContent = '';
      return;
    }

    resultsSizePill.textContent = `Size ${stanaSize}`;
    resultsSizePill.hidden = false;
  }

  function resetFeedback(root) {
    const resultsWrap = root.querySelector('[data-stana-calculator-results]');
    const emptyMessage = root.querySelector('[data-stana-calculator-empty]');
    const invalidMessage = root.querySelector('[data-stana-calculator-invalid]');
    const sizeSummary = root.querySelector('[data-stana-calculator-size-summary]');

    destroyCarousel(root);

    if (resultsWrap) resultsWrap.hidden = true;
    if (emptyMessage) emptyMessage.hidden = true;
    if (invalidMessage) invalidMessage.hidden = true;
    if (sizeSummary) sizeSummary.hidden = true;

    setResultsSizePill(root, null);
  }

  function formatSizeSummary(template, sizeResult, sizingChartHash) {
    const sizingChartUrl = `#${sizingChartHash || 'sizing'}`;
    const sizeChartLink = `<a href="${sizingChartUrl}" class="theme-link inline-block text-scheme-text stana-size-calculator__size-chart-link">View size chart</a>`;

    const summaryText = template
      .replace(/\{size_chart_link\}/g, '')
      .replace(/\{bra_size\}/g, sizeResult.braSize)
      .replace(/\{stana_size\}/g, String(sizeResult.stanaSize))
      .replace(/\{sizing_chart_url\}/g, sizingChartUrl)
      .trim();

    return `<span class="stana-size-calculator__size-summary-text">${summaryText}</span>${sizeChartLink}`;
  }

  function getInputMode(form) {
    const activeTab = form.querySelector('[data-stana-calculator-tab][aria-selected="true"]');
    return activeTab?.dataset.stanaCalculatorTab || 'measure';
  }

  function setPanelVisibility(form, mode) {
    const measurePanel = form.querySelector('[data-stana-calculator-panel="measure"]');
    const braPanel = form.querySelector('[data-stana-calculator-panel="bra"]');
    const unitFieldset = form.querySelector('[data-stana-calculator-units]');

    if (measurePanel) measurePanel.hidden = mode !== 'measure';
    if (braPanel) braPanel.hidden = mode !== 'bra';
    if (unitFieldset) unitFieldset.hidden = mode !== 'measure';

    const underband = form.querySelector('[data-stana-calculator-underband]');
    const bust = form.querySelector('[data-stana-calculator-bust]');
    const braSize = form.querySelector('[data-stana-calculator-bra-size]');

    if (underband) underband.required = mode === 'measure';
    if (bust) bust.required = mode === 'measure';
    if (braSize) braSize.required = mode === 'bra';
  }

  function setInputMode(form, mode) {
    form.querySelectorAll('[data-stana-calculator-tab]').forEach((tab) => {
      const isActive = tab.dataset.stanaCalculatorTab === mode;
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });

    setPanelVisibility(form, mode);
  }

  function showInvalid(root, invalidMessage, message) {
    resetFeedback(root);

    if (invalidMessage) {
      invalidMessage.hidden = false;
      invalidMessage.textContent = message;
    }
  }

  function showEmptyMessage(root, emptyMessage, invalidMessage, template, context) {
    resetFeedback(root);

    if (emptyMessage && template) {
      emptyMessage.innerHTML = formatMessage(template, context);
      emptyMessage.hidden = false;
      emptyMessage.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }

  function initCalculator(root) {
    const form = root.querySelector('[data-stana-calculator-form]');
    if (!form) return;

    const variants = enrichVariants(parseVariants(root));
    const lookup = parseLookup(root);
    const supportedRange = getSupportedRange(lookup);
    const sizingChartHash = root.dataset.sizingChartHash || 'sizing';
    const noResultsTemplate = readTemplate(
      root,
      '[data-stana-calculator-no-results-template]',
      DEFAULT_NO_RESULTS_TEMPLATE
    );
    const unsupportedTemplate = readTemplate(
      root,
      '[data-stana-calculator-unsupported-template]',
      DEFAULT_UNSUPPORTED_TEMPLATE
    );
    const resultsWrap = root.querySelector('[data-stana-calculator-results]');
    const resultsList = root.querySelector('[data-stana-calculator-results-list]');
    const emptyMessage = root.querySelector('[data-stana-calculator-empty]');
    const invalidMessage = root.querySelector('[data-stana-calculator-invalid]');
    const sizeSummary = root.querySelector('[data-stana-calculator-size-summary]');
    const sizeSummaryTemplate =
      root.dataset.sizeSummaryTemplate ||
      'Your Stana size: {stana_size} (fits {bra_size} bra size). {size_chart_link}';

    form.querySelectorAll('[data-stana-calculator-tab]').forEach((tab) => {
      tab.addEventListener('click', () => {
        setInputMode(form, tab.dataset.stanaCalculatorTab);
      });
    });

    setInputMode(form, getInputMode(form));
    resetFeedback(root);

    form.addEventListener('submit', (event) => {
      event.preventDefault();

      const mode = getInputMode(form);
      let sizeResult = null;
      let resolved = null;

      if (mode === 'bra') {
        const braValue = form.querySelector('[data-stana-calculator-bra-size]')?.value;
        resolved = resolveSizeFromBraInput(braValue, lookup);

        if (resolved.status === 'invalid') {
          showInvalid(
            root,
            invalidMessage,
            'Enter a valid UK bra size, for example 32F or 34DD.'
          );
          return;
        }

        if (resolved.status === 'unsupported') {
          showEmptyMessage(
            root,
            emptyMessage,
            invalidMessage,
            unsupportedTemplate,
            buildMessageContext(supportedRange, sizingChartHash, resolved.braSize)
          );
          return;
        }

        sizeResult = resolved.sizeResult;
      } else {
        const unit = form.querySelector('input[name="unit"]:checked')?.value || 'in';
        const underbandInches = toInches(form.querySelector('[data-stana-calculator-underband]')?.value, unit);
        const bustInches = toInches(form.querySelector('[data-stana-calculator-bust]')?.value, unit);

        if (underbandInches == null || bustInches == null) {
          showInvalid(
            root,
            invalidMessage,
            'Enter valid underband and bust measurements.'
          );
          return;
        }

        resolved = resolveSizeFromMeasurements(underbandInches, bustInches, lookup);

        if (resolved.status === 'invalid') {
          showInvalid(
            root,
            invalidMessage,
            'Enter valid underband and bust measurements.'
          );
          return;
        }

        if (resolved.status === 'unsupported') {
          showEmptyMessage(
            root,
            emptyMessage,
            invalidMessage,
            unsupportedTemplate,
            buildMessageContext(supportedRange, sizingChartHash, resolved.braSize)
          );
          return;
        }

        sizeResult = resolved.sizeResult;
      }

      if (invalidMessage) invalidMessage.hidden = true;

      const matches = matchVariants(variants, sizeResult);

      if (resultsList) {
        resultsList.innerHTML = '';
        matches.forEach((match) => {
          resultsList.appendChild(buildSlide(match));
        });
      }

      if (matches.length > 0) {
        if (sizeSummary) {
          sizeSummary.innerHTML = formatSizeSummary(sizeSummaryTemplate, sizeResult, sizingChartHash);
          sizeSummary.hidden = false;
        }
        setResultsSizePill(root, sizeResult.stanaSize);
        if (resultsWrap) resultsWrap.hidden = false;
        if (emptyMessage) emptyMessage.hidden = true;
        initCarousel(root, matches.length);
      } else {
        showEmptyMessage(
          root,
          emptyMessage,
          invalidMessage,
          noResultsTemplate,
          buildMessageContext(supportedRange, sizingChartHash, sizeResult.braSize)
        );
        return;
      }

      const scrollTarget = matches.length > 0 ? resultsWrap : sizeSummary || emptyMessage;
      if (scrollTarget && !scrollTarget.hidden) {
        scrollTarget.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    });
  }

  function initAll() {
    document.querySelectorAll('[data-stana-size-calculator]').forEach(initCalculator);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
})();
